import { useEffect, useRef } from 'react';

import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';

import { useAdBlockDetection } from './use-ad-block-detection';

type EligibilityTracking = {
  experimentEnabled: boolean;
  recentFacebookClick: boolean;
  metaConsentGiven: boolean;
  optOutCookieSet: boolean;
  checksSkipped: boolean;
  isLoading: boolean;
  attributionError: boolean;
  consentError: boolean;
};

const getCheckFlag = (
  value: boolean,
  skipped: boolean,
  hasError: boolean
): boolean | 'not_checked' | 'error' => {
  if (skipped) return 'not_checked';
  if (hasError) return 'error';
  return value;
};

export const useTrackContextualConsentEligibility = ({
  experimentEnabled,
  recentFacebookClick,
  metaConsentGiven,
  optOutCookieSet,
  checksSkipped,
  isLoading,
  attributionError,
  consentError,
}: EligibilityTracking): void => {
  const trackNwtEvent = useJobsNwtTracking();
  const { adBlockDetected, isLoading: isAdBlockDetectionLoading } =
    useAdBlockDetection();
  const hasTrackedEvaluation = useRef(false);

  useEffect(() => {
    if (
      (!checksSkipped && isLoading) ||
      isAdBlockDetectionLoading ||
      hasTrackedEvaluation.current
    ) {
      return;
    }

    hasTrackedEvaluation.current = true;
    trackNwtEvent({
      eventSchema: 'basic',
      event: 'contextual_consent_eligibility_evaluated',
      page: 'jobs/apply',
      flags: [
        `experiment_enabled_${experimentEnabled}`,
        `recent_facebook_click_${getCheckFlag(recentFacebookClick, checksSkipped, attributionError)}`,
        `meta_consent_given_${getCheckFlag(metaConsentGiven, checksSkipped, consentError)}`,
        `opt_out_cookie_set_${optOutCookieSet}`,
        `ad_blocker_detected_${adBlockDetected}`,
      ].join(','),
    });
  }, [
    adBlockDetected,
    attributionError,
    checksSkipped,
    consentError,
    experimentEnabled,
    isAdBlockDetectionLoading,
    isLoading,
    metaConsentGiven,
    optOutCookieSet,
    recentFacebookClick,
    trackNwtEvent,
  ]);
};
